// src/hooks/useVersionCheck.js
//
// Tells the app when a newer build of Decode has been deployed than the one this
// tab is running. Decode is a single-page app: after the first load a tab never
// fetches index.html again, so it keeps running whatever bundle it started with —
// for days, if the user never reloads. Create React App writes asset-manifest.json
// on every build (deployed alongside the app), naming the current content-hashed
// bundle; comparing that against the <script> tag on the page says whether a
// deploy has happened since this tab loaded. No backend is involved.
//
// Checks run on an interval and whenever the tab regains focus — the focus check
// is what catches a tab left open overnight the moment the user comes back to it.

import { useEffect, useState } from 'react';

const MANIFEST_URL = '/asset-manifest.json';
const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;

// The bundle this tab loaded, read from the <script> tag the build injects into
// index.html (e.g. "/static/js/main.d12adc4d.js").
const runningMainScript = () => (
  Array.from(document.scripts)
    .map((script) => script.getAttribute('src'))
    .find((src) => src && src.includes('/static/js/main.'))
  || null
);

export default function useVersionCheck({ intervalMs = DEFAULT_INTERVAL_MS } = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // The dev server serves an unhashed bundle.js and there is no deploy to compare
    // against, so the check only means something in a built bundle.
    if (process.env.NODE_ENV !== 'production') return undefined;
    const running = runningMainScript();
    if (!running) return undefined;

    let cancelled = false;
    let found = false;

    const check = async () => {
      if (found) return;
      try {
        // no-store: ask the CDN, never the browser cache — a cached manifest is the
        // very staleness this hook exists to detect.
        const response = await fetch(MANIFEST_URL, { cache: 'no-store' });
        if (!response.ok) return;
        const manifest = await response.json();
        const latest = manifest?.files?.['main.js'];
        if (!cancelled && latest && latest !== running) {
          found = true;
          setUpdateAvailable(true);
        }
      } catch (err) {
        // Offline or a dropped connection: a background poll, so the next tick
        // simply tries again. Nothing for the user to act on.
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    const timer = setInterval(check, intervalMs);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs]);

  return updateAvailable;
}
